/**
 * Data hooks for the /admin/onboarding wizard.
 *
 * Reads/writes onboarding_runs + onboarding_tasks directly via the admin's
 * authenticated session (admin RLS), matching the ghl_location_settings
 * pattern. The run page polls so waiting_on_client steps flip to done
 * without a manual refresh (the Stripe callback updates the task row from
 * an edge function).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import type { OnboardingRun, OnboardingTask } from '@limo/database'
import { buildTaskSeeds, type NewRunInputs } from '@/lib/onboarding/steps'

const RUN_REFRESH_INTERVAL = 5 * 1000

export interface OnboardingRunWithAccount extends OnboardingRun {
  accounts: { id: string; name: string; slug: string } | null
}

export function useOnboardingRuns() {
  return useQuery({
    queryKey: ['admin', 'onboarding-runs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('onboarding_runs')
        .select('*, accounts(id, name, slug)')
        .order('created_at', { ascending: false })
      if (error) throw new Error(error.message)
      return data as OnboardingRunWithAccount[]
    },
  })
}

export function useOnboardingRun(runId: string | undefined) {
  return useQuery({
    queryKey: ['admin', 'onboarding-run', runId],
    enabled: Boolean(runId),
    refetchInterval: RUN_REFRESH_INTERVAL,
    queryFn: async () => {
      const [runRes, tasksRes] = await Promise.all([
        supabase
          .from('onboarding_runs')
          .select('*, accounts(id, name, slug)')
          .eq('id', runId!)
          .single(),
        supabase
          .from('onboarding_tasks')
          .select('*')
          .eq('run_id', runId!),
      ])
      if (runRes.error) throw new Error(runRes.error.message)
      if (tasksRes.error) throw new Error(tasksRes.error.message)
      return {
        run: runRes.data as OnboardingRunWithAccount,
        tasks: tasksRes.data as OnboardingTask[],
      }
    },
  })
}

export interface CreateOnboardingRunInput extends NewRunInputs {
  accountId: string
}

export function useCreateOnboardingRun() {
  const queryClient = useQueryClient()
  const { profile } = useAuth()

  return useMutation({
    mutationFn: async (input: CreateOnboardingRunInput) => {
      const { data: run, error: runError } = await supabase
        .from('onboarding_runs')
        .insert({
          account_id: input.accountId,
          started_by: profile?.id ?? null,
        })
        .select('id')
        .single()
      if (runError) throw new Error(runError.message)

      const seeds = buildTaskSeeds(input).map((seed) => ({
        ...seed,
        run_id: run.id,
      }))
      const { error: tasksError } = await supabase
        .from('onboarding_tasks')
        .insert(seeds)
      if (tasksError) {
        // Roll back the half-created run so a retry starts clean.
        await supabase.from('onboarding_runs').delete().eq('id', run.id)
        throw new Error(tasksError.message)
      }

      return run.id as string
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'onboarding-runs'] })
    },
  })
}

/**
 * Invoke an onboarding step edge function (the step contract:
 * { accountId, runId } in → task result + status out) and refresh the run.
 */
export function useInvokeOnboardingStep(runId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      functionName,
      accountId,
      params,
    }: {
      functionName: string
      accountId: string
      params?: Record<string, unknown>
    }) => {
      const { data, error } = await supabase.functions.invoke(functionName, {
        body: { accountId, runId, params: params ?? {} },
      })
      if (error) throw new Error(error.message)
      if (data && data.success === false) {
        throw new Error(data.error ?? 'Step failed')
      }
      return data
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['admin', 'onboarding-run', runId],
      })
    },
  })
}
